import OpenAI from 'openai';

const apiKey = process.env.REACT_APP_OPENAI_API_KEY;

if (!apiKey) {
  console.warn('OpenAI API key not found in environment variables. Image analysis will not work.');
}

// Initialize the OpenAI client
export const openai = new OpenAI({
  apiKey: apiKey || 'dummy-key', // Fallback to prevent crashes, though it won't work
  dangerouslyAllowBrowser: true, // Allow usage in browser
});

export type ClothingItem = {
  type: string;
  color: string;
  pattern?: string;
  style?: string;
  season?: string[];
};

/**
 * Analyzes an image to identify and categorize clothing items using OpenAI's vision model
 * @param imageBase64 Base64 encoded image data
 * @returns Array of identified clothing items with their categories
 */
export async function analyzeClothingImage(imageBase64: string): Promise<ClothingItem[]> {
  if (!apiKey) {
    throw new Error('OpenAI API key not configured');
  }

  try {
    const response = await openai.chat.completions.create({
      model: "gpt-4o", // Updated to the latest model with vision capabilities
      messages: [
        {
          role: "user",
          content: [
            {
              type: "text",
              text: `Analyze this image and identify any clothing items. 
              For each item, provide the following details in a structured JSON format:
              1. Type (e.g., top, bottom, dress, outerwear, shoes, accessories)
              2. Color (be specific but concise)
              3. Pattern (if any, e.g., solid, striped, floral)
              4. Style (e.g., casual, formal, sporty, vintage)
              5. Suitable season (can be multiple: spring, summer, fall, winter)
              
              Format your response as a valid JSON array where each object has the properties: type, color, pattern, style, and season (as an array).
              Only output the JSON with no additional text.`
            },
            {
              type: "image_url",
              image_url: {
                url: `data:image/jpeg;base64,${imageBase64}`,
              },
            },
          ],
        },
      ],
      max_tokens: 1000,
    });

    const content = response.choices[0]?.message?.content;
    
    if (!content) {
      throw new Error('No response content from OpenAI');
    }

    // Extract JSON from the response
    try {
      // Find JSON in the response (GPT sometimes adds extra text)
      const jsonMatch = content.match(/\[[\s\S]*\]/);
      if (jsonMatch) {
        return JSON.parse(jsonMatch[0]) as ClothingItem[];
      }
      // Try parsing the whole response as JSON
      return JSON.parse(content) as ClothingItem[];
    } catch (error) {
      console.error('Failed to parse OpenAI response:', content);
      throw new Error('Failed to parse clothing analysis results');
    }
  } catch (error) {
    console.error('Error analyzing clothing image:', error);
    throw error;
  }
} 